import { useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { Alert, Badge, Button, Card, Col, Form, Modal, Row, Spinner, Table, FloatingLabel } from 'react-bootstrap'
import { apiClient } from '../services/api/client'

const toNum = (v) => {
  if (v === null || v === undefined || v === '') return 0
  const n = Number(String(v).replace(',', '.'))
  return Number.isFinite(n) ? n : 0
}

const toFixed2 = (v) => toNum(v).toFixed(2).replace('.', ',')

const formatLocalDate = (value) => {
  if (!value) return ''
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return String(value).slice(0, 10)
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

const splitText = (value) => {
  const text = String(value || '').trim()
  if (!text) return { first: '', rest: '' }
  const [first, ...rest] = text.split(/\s+/)
  return { first: first || '', rest: rest.join(' ') }
}

const recalcFulfillment = (item) => {
  const pocet = toNum(item.pocet)
  const uzvybmj = toNum(item.uzvybmj)
  const vybavitNum = Math.max(pocet - uzvybmj, 0)
  let vybavene = '0'
  if (vybavitNum === 0) {
    vybavene = '0'
  } else if (vybavitNum > 0 && vybavitNum < pocet) {
    vybavene = '1'
  } else if (vybavitNum >= pocet) {
    vybavene = '2'
  }
  return {
    vybavit: toFixed2(vybavitNum),
    vybavene
  }
}

const recalcRow = (item) => {
  const pocet = toNum(item.pocet)
  const cena = toNum(item.cena)
  const sleva = toNum(item.sleva)
  const dph = toNum(item.dph)
  const slevaKoef = 1 - sleva / 100
  const celkem = pocet * cena * slevaKoef
  const cenasdph = cena * (1 + dph / 100)
  const celkemsdph = celkem * (1 + dph / 100)
  const slevamj = cena * (sleva / 100)
  return {
    ...item,
    ...recalcFulfillment(item),
    slevamj: toFixed2(slevamj),
    cenasdph: toFixed2(cenasdph),
    celkem: toFixed2(celkem),
    celkemsdph: toFixed2(celkemsdph)
  }
}

const recalcTotals = (items) => {
  let zaklad21 = 0
  let zaklad12 = 0
  let zaklad0 = 0
  let dph21 = 0
  let dph12 = 0

  items.forEach((item) => {
    if (String(item.typradku || '1') !== '1') return
    const pocet = toNum(item.pocet)
    const cena = toNum(item.cena)
    const sleva = toNum(item.sleva)
    const dph = toNum(item.dph)
    const zaklad = pocet * cena * (1 - sleva / 100)
    const dphCastka = zaklad * (dph / 100)
    if (dph === 21) {
      zaklad21 += zaklad
      dph21 += dphCastka
    } else if (dph === 12) {
      zaklad12 += zaklad
      dph12 += dphCastka
    } else {
      zaklad0 += zaklad
    }
  })

  return {
    zaklad1: toNum(zaklad21).toFixed(2),
    dph1: toNum(dph21).toFixed(2),
    zaklad2: toNum(zaklad12).toFixed(2),
    dph2: toNum(dph12).toFixed(2),
    zaklad0: toNum(zaklad0).toFixed(2)
  }
}

const normalizeCategory = (value) => {
  return String(value || '')
    .toUpperCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim()
}

const normalizeStatus = (value) => String(value || '')
  .trim()
  .toLowerCase()
  .normalize('NFD')
  .replace(/[\u0300-\u036f]/g, '')
  .replace(/\s+/g, '')

const MATERIAL_CATS = [
  { code: 'ZAVAZI', icon: 'bi-database', label: 'Závaží­' },
  { code: 'VENTILY', icon: 'bi-wind', label: 'Ventily' },
  { code: 'ZAPLATY', icon: 'bi-bandaid', label: 'Záplaty' },
  { code: 'VLOZKY', icon: 'bi-disc', label: 'Vložky' },
  { code: 'HRIBKY', icon: 'bi-pin', label: 'Hříbky' },
  { code: 'OSTATNI', icon: 'bi-box', label: 'Ostatní' }
]

const SERVICE_TYPES = [
  { code: 'OS', icon: 'bi-car-front-fill', label: 'osobní' },
  { code: 'NA', icon: 'bi-truck-front-fill', label: 'nákladní' },
  { code: 'MO', icon: 'bi-car-front-fill', label: 'm.osobní' },
  { code: 'MN', icon: 'bi-truck-front-fill', label: 'm.nákladní' }
]
const SERVICE_GROUPS = { OS: ['STA', 'ALU', 'VAN', 'SUV', 'RFT'], NA: ['SIL', 'ZEM', 'EM'], MO: [], MN: [] }
const SERVICE_GROUP_LABELS = { STA: '13"–15"', ALU: '16"–17"', VAN: 'dodávky', SUV: '18"–19"', RFT: '20"–21"', SIL: 'silnice', ZEM: 'traktor', EM: 'stroje' }
const SERVICE_CATS = [
  { code: 'K1', icon: 'bi-rocket', label: 'komplety, de/montáž, vyvážení' },
  { code: 'K2', icon: 'bi-gear', label: 'opravy, ventilky a ostatní práce' },
  { code: 'K3', icon: 'bi-water', label: 'čištění' },
  { code: 'K4', icon: 'bi-speedometer2', label: 'huštění a TPMS' },
  { code: 'K5', icon: 'bi-signpost-2', label: 'výjezdy' },
  { code: 'K6', icon: 'bi-boxes', label: 'uskladnění' }
]

const mapOrderPayload = (payload) => {
  const h = payload?.header || {}
  const rows = Array.isArray(payload?.items) ? payload.items : []

  const header = {
    idobj: Number(h.IDOBJ),
    cislo: String(h.CISLO || '').trim(),
    origcislo: String(h.ORIGCISLO || '').trim(),
    ico: String(h.ICO || '').trim(),
    icoprij: String(h.ICOPRIJ || '').trim(),
    firma: String(h.FIRMA || '').trim(),
    spz: String(h.USRFLD1 || '').trim(),
    stani: String(h.USRFLD2 || '').trim(),
    stav: String(h.USRFLD3 || '').trim() || 'dilna',
    uhrada: String(h.USRFLD4 || '').trim() || 'hotovost',
    cislostr: String(h.STREDISKO ?? ''),
    zakazka: String(h.CISLO_ZAK ?? ''),
    datum: formatLocalDate(h.DATUM),
    vybavene: String(h.VYBAVENE ?? '2'),
    loguser: String(h.LOG_USER || '').trim(),
    poznamka: String(h.POZNAMKA || '').trim(),
    zaklad0: toNum(h.ZAKL0).toFixed(2),
    zaklad1: toNum(h.ZAKL1).toFixed(2),
    zaklad2: toNum(h.ZAKL2).toFixed(2),
    dph1: toNum(h.DPH1).toFixed(2),
    dph2: toNum(h.DPH2).toFixed(2)
  }

  const items = rows.map((r) => {
    const parsed = splitText(r.TEXT)
    const cena = toNum(r.CENAMJ)
    const dphPct = toNum(r.SADZBADPH)
    const dphMj = toNum(r.DPH)
    const typradku = String(r.TYP_RADKU || '1')
    const baseItem = {
      id: `${r.IDR || Date.now()}-${Math.random()}`,
      idrkar: r.IDRKAR ? String(r.IDRKAR) : '',
      karta: parsed.first,
      nazev: parsed.rest,
      mj: String(r.MJ || ''),
      pocet: toFixed2(r.POCETMJ),
      cena: toFixed2(cena),
      dph: String(dphPct || 0),
      sleva: toFixed2(r.ZLAVA),
      slevamj: toFixed2(r.SLEVAMJ),
      cenasdph: toFixed2(cena + dphMj),
      celkem: toFixed2(toNum(r.POCETMJ) * cena * (1 - toNum(r.ZLAVA) / 100)),
      celkemsdph: toFixed2(toNum(r.POCETMJ) * (cena + dphMj) * (1 - toNum(r.ZLAVA) / 100)),
      typpol: String(r.TYP_POL || ''),
      typradku,
      hmotnost: toNum(r.HMOTNOST).toFixed(4).replace('.', ','),
      vybavene: String(r.VYBAVENE ?? '2'),
      vybavit: toFixed2(r.VYBAVIT),
      uzvybmj: toFixed2(r.UZVYBMJ),
      locked: true
    }
    return recalcRow(baseItem)
  })

  return { header, items }
}

const MechanicOrderDetail = () => {
  const { id } = useParams()
  const navigate = useNavigate()
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')
  const [header, setHeader] = useState(null)
  const [items, setItems] = useState([])

  const [showServiceModal, setShowServiceModal] = useState(false)
  const [servicesAll, setServicesAll] = useState([])
  const [servicesLoaded, setServicesLoaded] = useState(false)
  const [serviceTyp, setServiceTyp] = useState('OS')
  const [serviceSku, setServiceSku] = useState('STA')
  const [serviceKat, setServiceKat] = useState('K1')
  const [activeService, setActiveService] = useState(null)
  const [serviceQty, setServiceQty] = useState('1')

  const [showMaterialModal, setShowMaterialModal] = useState(false)
  const [materialAll, setMaterialAll] = useState([])
  const [materialLoaded, setMaterialLoaded] = useState(false)
  const [materialCat, setMaterialCat] = useState('ZAVAZI')
  const [activeMaterial, setActiveMaterial] = useState(null)
  const [materialQty, setMaterialQty] = useState('1')

  const [showCloseConfirm, setShowCloseConfirm] = useState(false)
  const [showQtyEditor, setShowQtyEditor] = useState(false)
  const [qtyEditorItemId, setQtyEditorItemId] = useState(null)
  const [qtyEditorValue, setQtyEditorValue] = useState('1')

  const loadDetail = async () => {
    try {
      setLoading(true)
      setError('')
      const payload = await apiClient.get(`/mrp/nactiobjednavka?idobj=${encodeURIComponent(id)}`)
      const mapped = mapOrderPayload(payload)
      setHeader(mapped.header)
      setItems(mapped.items)
    } catch (e) {
      setError(e.message || 'Nepodařilo se načíst objednávku.')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (!id) return
    loadDetail()
  }, [id])

  useEffect(() => {
    if (!showServiceModal || servicesLoaded) return
    const loadServices = async () => {
      try {
        const data = await apiClient.get('/mrp/sluzby')
        setServicesAll(Array.isArray(data) ? data : [])
        setServicesLoaded(true)
      } catch (e) {
        setServicesAll([])
        setError(e.message || 'Nepodařilo se načíst služby.')
      }
    }
    loadServices()
  }, [showServiceModal, servicesLoaded])

  useEffect(() => {
    if (!showMaterialModal || materialLoaded) return
    const loadMaterial = async () => {
      try {
        const data = await apiClient.get('/mrp/material')
        setMaterialAll(Array.isArray(data) ? data : [])
        setMaterialLoaded(true)
      } catch (e) {
        setMaterialAll([])
        setError(e.message || 'Nepodařilo se načíst materiál.')
      }
    }
    loadMaterial()
  }, [showMaterialModal, materialLoaded])

  const selectedMaterialCards = useMemo(() => {
    return new Set(items.map((x) => String(x.karta || '')))
  }, [items])

  const filteredServices = useMemo(() => {
    return servicesAll.filter((service) => {
      const matchesVehicle = service.typ === serviceTyp && (service.sku === serviceSku || service.sku === 'ALL')
      const matchesCategory = service.kat === serviceKat
      return matchesVehicle && matchesCategory
    })
  }, [servicesAll, serviceTyp, serviceSku, serviceKat])

  const filteredMaterial = useMemo(() => {
    return materialAll.filter((m) => normalizeCategory(m.kat) === materialCat)
  }, [materialAll, materialCat])

  const QUICK_QTY_PRESETS = ['1', '2', '3', '4']
  const STEPPER_STEP = 1

  const normalizeQtyInput = (value) => {
    const digitsOnly = String(value || '').replace(/\D/g, '')
    if (!digitsOnly) return '0'
    return String(Math.max(parseInt(digitsOnly, 10) || 0, 0))
  }

  const changeMaterialQtyByStep = (delta) => {
    const next = Math.max(Math.round(toNum(materialQty)) + delta, 0)
    setMaterialQty(String(next))
  }

  const addService = () => {
    if (!activeService || selectedMaterialCards.has(String(activeService.karta))) return
    const newItem = recalcRow({
      id: `s-${Date.now()}-${Math.random()}`,
      idrkar: String(activeService.id || ''),
      karta: String(activeService.karta || ''),
      nazev: String(activeService.nazev || ''),
      mj: String(activeService.mj || 'ks'),
      pocet: toFixed2(serviceQty || 1),
      cena: toFixed2(activeService.cena),
      dph: String(activeService.dph || 0),
      sleva: '0,00',
      slevamj: '0,00',
      cenasdph: '',
      celkem: '',
      celkemsdph: '',
      typpol: String(activeService.typpol || 'S'),
      typradku: '1',
      hmotnost: toNum(activeService.hmotnost).toFixed(4).replace('.', ','),
      uzvybmj: '0,00',
      vybavit: toFixed2(serviceQty || 1),
      vybavene: '2',
      locked: false
    })
    setItems((prev) => [...prev, newItem])
    setActiveService(null)
    setServiceQty('1')
  }

  const closeServiceModal = () => {
    setShowServiceModal(false)
    setActiveService(null)
    setServiceQty('1')
  }

  const onChangeItemQty = (itemId, value) => {
    setItems((prev) =>
      prev.map((item) => {
        if (item.id !== itemId || item.locked) return item
        return recalcRow({ ...item, pocet: value })
      })
    )
  }

  const removeEditableItem = (itemId) => {
    setItems((prev) => prev.filter((item) => !(item.id === itemId && !item.locked)))
  }

  const openQtyEditor = (item) => {
    if (!item || item.locked) return
    setQtyEditorItemId(item.id)
    setQtyEditorValue(String(Math.max(Math.round(toNum(item.pocet)), 0)))
    setShowQtyEditor(true)
  }

  const closeQtyEditor = () => {
    setShowQtyEditor(false)
    setQtyEditorItemId(null)
    setQtyEditorValue('1')
  }

  const changeQtyEditorByStep = (delta) => {
    const next = Math.max(Math.round(toNum(qtyEditorValue)) + delta, 0)
    setQtyEditorValue(String(next))
  }

  const applyQtyEditor = () => {
    if (!qtyEditorItemId) return
    onChangeItemQty(qtyEditorItemId, toFixed2(normalizeQtyInput(qtyEditorValue)))
    closeQtyEditor()
  }

  const addMaterial = () => {
    if (!activeMaterial) return
    if (selectedMaterialCards.has(String(activeMaterial.karta))) return

    const newItem = recalcRow({
      id: `m-${Date.now()}-${Math.random()}`,
      idrkar: String(activeMaterial.id || ''),
      karta: String(activeMaterial.karta || ''),
      nazev: String(activeMaterial.nazev || ''),
      mj: String(activeMaterial.mj || 'ks'),
      pocet: toFixed2(materialQty || 1),
      cena: toFixed2(activeMaterial.cena),
      dph: String(activeMaterial.dph || 0),
      sleva: '0,00',
      slevamj: '0,00',
      cenasdph: '',
      celkem: '',
      celkemsdph: '',
      typpol: String(activeMaterial.typpol || 'PP'),
      typradku: '1',
      hmotnost: toNum(activeMaterial.hmotnost).toFixed(4).replace('.', ','),
      uzvybmj: '0,00',
      vybavit: toFixed2(materialQty || 1),
      vybavene: '2',
      locked: false
    })

    setItems((prev) => [...prev, newItem])
    setActiveMaterial(null)
    setMaterialQty('1')
  }

  const closeMaterialModal = () => {
    setShowMaterialModal(false)
    setActiveMaterial(null)
    setMaterialQty('1')
  }

  const handleCloseOrder = async () => {
    if (!header) return
    try {
      setSaving(true)
      setError('')
      setSuccess('')

      const normalRows = items.filter((x) => String(x.typradku || '1') === '1')
      const allDone = normalRows.length > 0 && normalRows.every((x) => String(x.vybavene) === '0')
      const allOpen = normalRows.length > 0 && normalRows.every((x) => String(x.vybavene) === '2')
      const finalHeaderVybavene = allDone ? '0' : allOpen ? '2' : '1'
      const totals = recalcTotals(items)

      const payload = {
        ...header,
        ...totals,
        vybavene: finalHeaderVybavene,
        stav: 'provedeno',
        polozky: items.map((item) => ({
          idrkar: item.idrkar || '',
          karta: item.karta || '',
          nazev: item.nazev || '',
          mj: item.mj || '',
          pocet: item.pocet || '0,00',
          cena: item.cena || '0,00',
          sleva: item.sleva || '0,00',
          slevamj: item.slevamj || '0,00',
          dph: item.dph || '0',
          cenasdph: item.cenasdph || '0,00',
          celkemsdph: item.celkemsdph || '0,00',
          typpol: item.typpol || '',
          typradku: item.typradku || '1',
          hmotnost: item.hmotnost || '0,0000',
          vybavene: item.vybavene || '2',
          vybavit: item.vybavit || '0,00',
          uzvybmj: item.uzvybmj || '0,00'
        }))
      }

      await apiClient.put('/mrp/zmenaobjednavka', payload)
      setSuccess('Objednávka byla uzavřena.')
      navigate('/dashboard')
    } catch (e) {
      setError(e.message || 'Uzavření objednávky se nepodařilo.')
    } finally {
      setSaving(false)
      setShowCloseConfirm(false)
    }
  }

  if (loading) {
    return (
      <div className="text-center py-5">
        <Spinner />
      </div>
    )
  }

  if (!header) {
    return <Alert variant="danger">Objednávku se nepodařilo načíst.</Alert>
  }

  const isCompleted = normalizeStatus(header.stav) === 'provedeno'

  return (
    <div className=' p-2'>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <Button className='btn btn-lg' type="button" variant="secondary" onClick={() => navigate('/dashboard')}>
          <i className="bi bi-arrow-left me-2"></i>
          Zpět
        </Button>
        <Badge bg="primary-subtle" pill className="text-secondary p-3 px-4 fs-5">
          {header.cislo || '-'}
        </Badge>
      </div>

      {error && <Alert variant="danger">{error}</Alert>}
      {success && <Alert variant="success">{success}</Alert>}

      <div className="mb-2 mechanic-detail-header p-2">
        <div className="d-flex align-items-start flex-nowrap overflow-auto">
          <div className="mechanic-detail-header-col flex-shrink-0">
            <div className="small text-muted">SPZ</div>
            <div className="fw-semibold text-secondary text-nowrap">{header.spz || '-'}</div>
          </div>
          <div className="mechanic-detail-header-col flex-grow-1" style={{ minWidth: 180 }}>
            <div className="small text-muted">Zákazník</div>
            <div className="fw-semibold text-secondary text-truncate" title={header.firma || '-'}>
              {header.firma || '-'}
            </div>
          </div>
          <div className="mechanic-detail-header-col flex-shrink-0">
            <div className="small text-muted">Stání</div>
            <div className="fw-semibold text-secondary text-nowrap">{header.stani || '-'}</div>
          </div>
        </div>
      </div>

      <Table responsive className="align-middle mt-2 mb-3 mechanic-detail-table">
        <thead className=''>
          <tr>
            <th className='text-start' style={{ width: '20%' }}>Číslo karty</th>
            <th className='text-start' style={{ width: '58%' }}>Název</th>
            <th className='text-start' style={{ width: '15%' }}>Počet MJ</th>
            <th className='text-center' style={{ width: '7%' }}></th>
          </tr>
        </thead>
        <tbody>
          {items.map((item) => (
            <tr key={item.id}>
              <td className='p-1 ps-0 pb-0'>
                <Form.Control className="p-3" value={item.karta || ''} readOnly disabled />
              </td>
              <td className='p-1 pb-0'>
                <Form.Control className="p-3" value={item.nazev || ''} readOnly disabled />
              </td>
              <td className='p-1 pe-0 pb-0' colSpan={item.locked ? 2 : 1}>
                <Form.Control
                  value={item.pocet || ''}
                  className="p-3"
                  onClick={() => openQtyEditor(item)}
                  readOnly
                  disabled={item.locked}
                  style={{ cursor: item.locked ? 'not-allowed' : 'pointer' }}
                />
              </td>
              {!item.locked && (
                <td className='p-1 pe-0 pb-0 text-center'>
                  <Button
                    type="button"
                    variant="outline-danger"
                    className="p-3"
                    title="Odstranit řádek"
                    onClick={() => removeEditableItem(item.id)}
                  >
                    <i className="bi bi-trash"></i>
                  </Button>
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </Table>

      {!isCompleted && (
        <div className="d-flex flex-wrap gap-1">
          <Button className='btn btn-lg' type="button" variant="outline-primary" onClick={() => setShowServiceModal(true)}><i className="bi bi-plus-circle me-2"></i>Služby</Button>
          <Button className='btn btn-lg' type="button" variant="outline-primary" onClick={() => setShowMaterialModal(true)}><i className="bi bi-plus-circle me-2"></i>Materiál</Button>
          <Button className='btn btn-lg' type="button" variant="success" onClick={() => setShowCloseConfirm(true)} disabled={saving}><i className="bi bi-check2-square me-2"></i>{saving ? 'Ukládám...' : 'Uzavřít'}</Button>
        </div>
      )}

      <Modal contentClassName='p-2' show={showQtyEditor} onHide={closeQtyEditor} keyboard={true} centered>
        <Modal.Header closeButton>
          <Modal.Title className='lh-1 text-primary'>Počet MJ<br/>
            <span className='fs-6 text-muted'>úprava počtu MJ položky</span>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="d-flex align-items-center justify-content-center gap-2 my-3">
            <Button
              type="button"
              variant="outline-secondary"
              className="px-4 py-3 fs-3"
              onClick={() => changeQtyEditorByStep(-STEPPER_STEP)}
            >
              <i className="bi bi-dash-lg"></i>
            </Button>
            <Form.Control
              type="text"
              value={qtyEditorValue}
              className="text-center fs-3 py-3"
              style={{ maxWidth: 100 }}
              onFocus={(e) => setTimeout(() => e.target.select(), 0)}
              onChange={(e) => setQtyEditorValue(normalizeQtyInput(e.target.value))}
            />
            <Button
              type="button"
              variant="outline-secondary"
              className="px-4 py-3 fs-3"
              onClick={() => changeQtyEditorByStep(STEPPER_STEP)}
            >
              <i className="bi bi-plus-lg"></i>
            </Button>
          </div>
          <div className="d-flex justify-content-center gap-2 flex-wrap mb-3">
            {QUICK_QTY_PRESETS.map((qty) => (
              <Button
                key={`editor-${qty}`}
                type="button"
                variant={qtyEditorValue === qty ? 'primary' : 'outline-primary'}
                className="btn-square px-4 py-2 fs-4"
                onClick={() => setQtyEditorValue(qty)}
              >
                {qty}
              </Button>
            ))}
          </div>
          <div className='mt-5'>
            <Button 
            className='btn btn-lg' 
            type="button" 
            variant="secondary" 
            onClick={closeQtyEditor}
            >
              <i className="bi bi-arrow-left me-2"></i>Zpět
            </Button>
            <Button
              type="button"
              className='btn btn-lg ms-1'
              variant="primary"
              onClick={applyQtyEditor}
              disabled={toNum(qtyEditorValue) <= 0}
            >
              <i className="bi bi-check2-circle me-1"></i> Upravit
            </Button>
          </div>          
        </Modal.Body>
      </Modal>

      {/* Přidat novou službu */}
      <Modal show={showServiceModal} fullscreen="xxl-down" onHide={closeServiceModal} keyboard={true} size='xl' centered>
        <Modal.Header closeButton className="border-0 p-4">
          <Modal.Title className="text-primary lh-1">
            Služby<br />
            <span className="text-muted fs-6">výběr servisních úkonů</span>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="d-flex flex-column">
          <div className="d-flex justify-content-center align-items-start flex-wrap gap-4 mb-3">
            <div><div className="service-section-title">Typ vozidla</div><div className="d-flex gap-1 flex-wrap">{SERVICE_TYPES.map((type) => <Button key={type.code} className="btn-square p-1" variant={serviceTyp === type.code ? 'primary' : 'outline-primary'} onClick={() => { setServiceTyp(type.code); setServiceSku(SERVICE_GROUPS[type.code]?.[0] || ''); setServiceKat('K1'); setActiveService(null) }}><i className={`bi ${type.icon}`}></i><div className="small mt-1">{type.label}</div></Button>)}</div></div>
            {SERVICE_GROUPS[serviceTyp]?.length ? <div><div className="service-section-title">Skupina vozidel</div><div className="d-flex gap-1 flex-wrap">{SERVICE_GROUPS[serviceTyp].map((group) => <Button key={group} className="btn-square p-1" variant={serviceSku === group ? 'primary' : 'outline-primary'} onClick={() => { setServiceSku(group); setActiveService(null) }}><strong>{group}</strong><div className="small mt-1">{SERVICE_GROUP_LABELS[group]}</div></Button>)}</div></div> : null}
          </div>
          <div className="service-picker-layout overflow-auto">
            <div className="service-category-menu"><div className="service-section-title">Kategorie služby</div>{SERVICE_CATS.map((category) => <Button key={category.code} className="service-category-tile text-start" variant={serviceKat === category.code ? 'primary' : 'outline-primary'} onClick={() => { setServiceKat(category.code); setActiveService(null) }}><i className={`bi ${category.icon} service-category-icon`}></i><span className="service-category-label">{category.label}</span></Button>)}</div>
            <div className="service-card-grid"><div className="service-section-title service-card-grid-title">Nabídka služeb</div>
              {filteredServices.map((service) => {
                const selectedItem = items.find((item) => String(item.karta || '') === String(service.karta || ''))
                const selected = Boolean(selectedItem)
                const active = activeService?.id === service.id
                return (
                  <div
                    key={service.id}
                    role={selected ? undefined : 'button'} tabIndex={selected ? -1 : 0}
                    className={`service-card-tile text-start ${selected ? 'is-selected' : active ? 'is-active' : ''}`}
                    onClick={() => !selected && setActiveService(service)}
                    title={selected ? 'Služba už je v objednávce' : service.nazev}
                  >
                    <div className="service-card-main"><span className="service-card-number">{service.karta}</span><span className="service-card-name">{service.nazev}</span></div>
                    <div className="service-card-side"><span className="service-card-price">{Number(service.cena || 0).toFixed(0)} Kč</span>{selectedItem && !selectedItem.locked ? <button type="button" className="service-card-delete" onClick={(event) => { event.stopPropagation(); removeEditableItem(selectedItem.id) }}><i className="bi bi-trash3"></i></button> : <span />}</div>
                  </div>
                )
              })}
            </div>
          </div>
          <div className="service-selection-panel mt-auto pt-3">
              <hr />
              <div className="service-section-title">Vybraná položka</div><div className={activeService ? 'text-primary mb-2' : 'text-muted mb-2'}>{activeService ? `${activeService.karta} ${activeService.nazev}` : 'Nejprve vyberte službu z nabídky.'}</div>
              <div className="d-flex align-items-center gap-2 flex-nowrap overflow-auto pb-1">
                <Button type="button" variant="outline-secondary" className="mechanic-qty-button fs-5" disabled={!activeService} onClick={() => setServiceQty(String(Math.max(Math.round(toNum(serviceQty)) - STEPPER_STEP, 0)))}><i className="bi bi-dash-lg"></i></Button>
                <Form.Control type="text" value={serviceQty} disabled={!activeService} className="mechanic-qty-input text-center fs-5" onFocus={(e) => setTimeout(() => e.target.select(), 0)} onChange={(event) => setServiceQty(normalizeQtyInput(event.target.value))} />
                <Button type="button" variant="outline-secondary" className="mechanic-qty-button fs-5" disabled={!activeService} onClick={() => setServiceQty(String(Math.round(toNum(serviceQty)) + STEPPER_STEP))}><i className="bi bi-plus-lg"></i></Button>
                {QUICK_QTY_PRESETS.map((qty) => <Button key={`service-${qty}`} type="button" variant={serviceQty === qty ? 'primary' : 'outline-primary'} className="mechanic-qty-preset fs-4" disabled={!activeService} onClick={() => setServiceQty(qty)}>{qty}</Button>)}
                <Button type="button" className="mechanic-qty-add btn-lg px-4" onClick={addService} disabled={!activeService || toNum(serviceQty) <= 0}><i className={`bi ${activeService ? 'bi-plus-circle' : 'bi-slash-circle'} me-2`}></i>{activeService ? 'Přidat' : 'Vyberte položku'}</Button>
              </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button className='btn btn-lg px-4' type="button" variant="secondary" onClick={closeServiceModal}><i className="bi bi-arrow-left me-2"></i>Zpět</Button>
        </Modal.Footer>
      </Modal>
      
      {/* Přidat nový materiál */}
      <Modal show={showMaterialModal} fullscreen="xxl-down" onHide={closeMaterialModal} keyboard={true} size='xl' centered>
        <Modal.Header closeButton className="border-0 p-4">
          <Modal.Title className="text-primary lh-1">
            Opravný materiál<br />
            <span className="text-muted fs-6">výběr spotřebního materiálu</span>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="d-flex flex-column">
          <div className="service-picker-layout overflow-auto">
              <div className="service-category-menu"><div className="service-section-title">Kategorie materiálu</div>
                {MATERIAL_CATS.map((c) => (
                  <Button
                    key={c.code}
                    type="button"
                    className="service-category-tile text-start"
                    variant={materialCat === c.code ? 'primary' : 'outline-primary'}
                    onClick={() => {
                      setMaterialCat(c.code)
                      setActiveMaterial(null)
                    }}
                  >
                    <i className={`bi ${c.icon} service-category-icon`}></i><span className="service-category-label">{c.label}</span>
                  </Button>
                ))}
              </div>
              <div className="service-card-grid"><div className="service-section-title service-card-grid-title">Nabídka materiálu</div>
                {filteredMaterial.map((m) => {
                  const selectedItem = items.find((item) => String(item.karta || '') === String(m.karta || ''))
                  const selected = Boolean(selectedItem)
                  const active = activeMaterial?.id === m.id 
                  return (
                    <div
                      key={m.id}
                      role={selected ? undefined : 'button'} tabIndex={selected ? -1 : 0}
                      className={`service-card-tile text-start ${selected ? 'is-selected' : active ? 'is-active' : ''}`}
                      onClick={() => !selected && setActiveMaterial(m)}
                      title={selected ? 'Materiál už je v objednávce' : m.nazev}  
                    >
                      <div className="service-card-main"><span className="service-card-number">{m.karta}</span><span className="service-card-name">{m.nazev}</span></div>
                      <div className="service-card-side"><span className="service-card-price">{Number(m.cena || 0).toFixed(0)} Kč</span>{selectedItem && !selectedItem.locked ? <button type="button" className="service-card-delete" onClick={(event) => { event.stopPropagation(); removeEditableItem(selectedItem.id) }}><i className="bi bi-trash3"></i></button> : <span />}</div>
                    </div>
                  )
                })}
              </div>
            </div>
            <div className="service-selection-panel mt-auto px-2">
              <hr className="hr" />
              <Row className="g-2 align-items-end">
                <Col xs={12}>
                  <div className="service-section-title">Vybraná položka</div><div className={activeMaterial ? 'text-primary mb-2' : 'text-muted mb-2'}>{activeMaterial ? `${activeMaterial.karta} ${activeMaterial.nazev}` : 'Nejprve vyberte materiál z nabídky.'}</div>
                </Col>    
                <Col xs={12}>
                  <div className="d-flex align-items-center gap-2 flex-nowrap overflow-auto pb-1">
                    <Button
                      type="button"
                      variant="outline-secondary"
                      className="mechanic-qty-button fs-5"
                      disabled={!activeMaterial}
                      onClick={() => changeMaterialQtyByStep(-STEPPER_STEP)}
                    >
                      <i className="bi bi-dash-lg"></i>
                    </Button>
                    <Form.Control
                      type="text"
                      value={materialQty}
                      disabled={!activeMaterial}
                      className="mechanic-qty-input text-center fs-5"
                      onFocus={(e) => setTimeout(() => e.target.select(), 0)}
                      onChange={(e) => setMaterialQty(normalizeQtyInput(e.target.value))}
                    />
                    <Button
                      type="button"
                      variant="outline-secondary"
                      className="mechanic-qty-button fs-5"
                      disabled={!activeMaterial}
                      onClick={() => changeMaterialQtyByStep(STEPPER_STEP)}
                    >
                      <i className="bi bi-plus-lg"></i>
                    </Button>

                    {QUICK_QTY_PRESETS.map((qty) => (
                      <Button
                        key={qty}
                        type="button"
                        variant={materialQty === qty ? 'primary' : 'outline-primary'}
                        className="mechanic-qty-preset fs-4"
                        disabled={!activeMaterial}
                        onClick={() => setMaterialQty(qty)}
                      >
                        {qty}
                      </Button>
                    ))}

                    <Button
                      className="mechanic-qty-add btn-lg px-4"
                      onClick={addMaterial}
                      disabled={!activeMaterial || toNum(materialQty) <= 0}
                      title={activeMaterial ? 'Přidat položku' : 'Nejprve vyberte položku'}
                    >
                      <i className={`bi ${activeMaterial ? 'bi-plus-circle' : 'bi-slash-circle'} me-2`}></i>
                      {activeMaterial ? 'Přidat' : 'Vyberte položku'}
                    </Button>
                  </div>
                </Col>
              </Row>
            </div>
        </Modal.Body>
        <Modal.Footer>
          <Button className='btn btn-lg px-4' type="button" variant="secondary" onClick={closeMaterialModal}>
            <i className="bi bi-arrow-left me-2"></i>Zpět
          </Button>          
        </Modal.Footer>
      </Modal>

      {/* Kontrola před uzavřením */}
      <Modal
        show={showCloseConfirm}
        onHide={() => setShowCloseConfirm(false)}
        keyboard={true}
        centered
        contentClassName="bg-warning-subtle border border-warning p-2"
      >
        <Modal.Header closeButton className="bg-warning-subtle border-warning-subtle">
          <Modal.Title className='fw-semibold lh-1'>Kontrola<br/>
            <span className='fs-6'>povinná kontrola před uzavřením</span>
          </Modal.Title> 
        </Modal.Header>
        <Modal.Body className="bg-warning-subtle text-center">
          <div className='fs-2 fw-semibold mt-3'>Před uzavřením objednávky je nutné dotáhnout všechna kola momentovým klíčem!</div>
          <div className='mt-5 text-start'>
            <Button className='btn btn-lg me-1' type="button" variant="secondary" onClick={() => setShowCloseConfirm(false)}>
              <i className="bi bi-arrow-left me-2"></i>Zpět
            </Button>
            <Button className='btn btn-lg' type="button" variant="success" onClick={handleCloseOrder} disabled={saving}>
              <i className="bi bi-check2-circle me-2"></i>
              Provedeno a zkontrolováno
            </Button>
          </div>
        </Modal.Body>       
      </Modal>
    </div>
  )
}

export default MechanicOrderDetail
